import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Moon } from "lucide-react";

import { SHOP } from "./data";
import { EASE, Em, MaskLines, Reveal, SectionTag } from "./primitives";
import ShopMini, { DESKTOP, PHONE, ScaledStage } from "./ShopMini";

const EASE_WIPE = [0.65, 0, 0.35, 1];

const BRAND_COLORS = [
   { hex: "#8C2F1B", name: "Bordeaux" },
   { hex: "#2F5D3A", name: "Bosgroen" },
   { hex: "#B8742A", name: "Oker" },
   { hex: "#1F3A8C", name: "Marine" },
   { hex: "#1d1d1f", name: "Zwart" },
];

// Zelfde thema's als in de echte shop (src/themes), donkere modus is een aparte schakelaar
const OPTIONS = [
   { id: "modern", label: "Modern", text: "Licht en rustig, afgeronde kaarten, zwart-wit." },
   { id: "classic", label: "Klassiek kader", text: "Als de menukaart van een Franse brasserie, in je eigen merkkleur." },
];

export default function ThemeShowcase() {
   const [theme, setTheme] = useState("classic");
   const [brand, setBrand] = useState(BRAND_COLORS[0].hex);
   const [dark, setDark] = useState(false);
   const key = `${theme}-${theme === "classic" ? brand : ""}-${dark}`;

   return (
      <section id="huisstijl" className="lp-grain lp-grain-dark relative overflow-hidden bg-lp-ink px-5 py-28 text-lp-paper sm:px-8 sm:py-40">
         <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-4">
               <SectionTag dark>Huisstijl</SectionTag>
               <MaskLines
                  as="h2"
                  className="mt-8 text-[clamp(2.8rem,5.6vw,5.2rem)] font-semibold leading-[0.92] tracking-[-0.05em]"
                  lines={["Jouw zaak. ", <Em className="text-lp-blue">Jouw stijl.</Em>]}
               />
               <Reveal>
                  <p className="mt-6 max-w-sm text-lg leading-relaxed text-lp-paper/60">
                     Kies een thema, zet je logo en foto's erin en je webshop voelt als je eigen zaak. Dit is de echte
                     shop, in het klein. Probeer maar.
                  </p>
               </Reveal>

               <div className="mt-10 space-y-2" role="radiogroup" aria-label="Thema">
                  {OPTIONS.map((o) => {
                     const on = theme === o.id;
                     return (
                        <button
                           key={o.id}
                           role="radio"
                           aria-checked={on}
                           onClick={() => setTheme(o.id)}
                           className={`group relative flex w-full items-start gap-4 rounded-2xl px-5 py-4 text-left transition-colors ${
                              on ? "" : "hover:bg-white/[0.04]"
                           }`}
                        >
                           {on && (
                              <motion.span layoutId="theme-pick" className="absolute inset-0 rounded-2xl bg-white/[0.08] ring-1 ring-white/10" transition={{ duration: 0.5, ease: EASE }} />
                           )}
                           <span className={`relative mt-1.5 flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full border ${on ? "border-lp-blue" : "border-white/30"}`}>
                              {on && <motion.span layoutId="theme-dot" className="h-1.5 w-1.5 rounded-full bg-lp-blue" />}
                           </span>
                           <span className="relative">
                              <span className={`block text-lg font-medium ${on ? "text-lp-paper" : "text-lp-paper/60"}`}>{o.label}</span>
                              <span className="mt-0.5 block text-sm text-lp-paper/45">{o.text}</span>
                           </span>
                        </button>
                     );
                  })}
               </div>

               <AnimatePresence initial={false}>
                  {theme === "classic" && (
                     <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="overflow-hidden"
                     >
                        <p className="mt-6 font-ticket text-[10px] uppercase tracking-[0.18em] text-lp-paper/45">Merkkleur</p>
                        <div className="mt-3 flex gap-3 pb-1 pl-1">
                           {BRAND_COLORS.map((b) => (
                              <button
                                 key={b.hex}
                                 onClick={() => setBrand(b.hex)}
                                 aria-label={b.name}
                                 title={b.name}
                                 className={`h-9 w-9 rounded-full ring-offset-2 ring-offset-lp-ink transition-transform hover:scale-110 ${
                                    brand === b.hex ? "ring-2 ring-lp-paper" : "ring-1 ring-white/15"
                                 }`}
                                 style={{ background: b.hex }}
                              />
                           ))}
                        </div>
                     </motion.div>
                  )}
               </AnimatePresence>

               <button
                  role="switch"
                  aria-checked={dark}
                  onClick={() => setDark((d) => !d)}
                  className="mt-6 flex w-full items-center gap-4 rounded-2xl px-5 py-4 text-left transition-colors hover:bg-white/[0.04]"
               >
                  <Moon size={16} className={dark ? "text-lp-blue" : "text-lp-paper/50"} />
                  <span className="flex-1">
                     <span className={`block text-lg font-medium ${dark ? "text-lp-paper" : "text-lp-paper/60"}`}>Donkere modus</span>
                     <span className="mt-0.5 block text-sm text-lp-paper/45">Voor elk thema. Je gerechten springen eruit.</span>
                  </span>
                  <span className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${dark ? "bg-lp-blue" : "bg-white/15"}`}>
                     <motion.span
                        animate={{ x: dark ? 20 : 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 32 }}
                        className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white"
                     />
                  </span>
               </button>
            </div>

            <Reveal y={60} className="lg:col-span-8">
               <div className="overflow-hidden rounded-[22px] bg-[#2a2826] p-1.5 shadow-[0_80px_120px_-50px_rgba(0,0,0,0.9)] ring-1 ring-white/10">
                  <div className="flex items-center gap-2 px-3 py-2.5">
                     <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                     <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                     <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                     <span className="ml-3 flex-1 truncate rounded-md bg-black/30 px-3 py-1 text-center font-ticket text-[11px] text-lp-paper/50">
                        resto-online.be/{SHOP.slug}
                     </span>
                  </div>
                  <ScaledStage pick={(w) => (w && w < 560 ? PHONE : DESKTOP)} className="rounded-[16px]">
                     {(cfg) => (
                        <AnimatePresence initial={false}>
                           <motion.div
                              key={`${key}-${cfg.width}`}
                              className="absolute inset-0"
                              style={{ zIndex: 1 }}
                              initial={{ clipPath: "circle(0% at 0% 0%)" }}
                              animate={{ clipPath: "circle(145% at 0% 0%)", zIndex: 1 }}
                              exit={{ zIndex: 0, opacity: 0.99, transition: { duration: 1 } }}
                              transition={{ duration: 1, ease: EASE_WIPE }}
                           >
                              <ShopMini theme={theme} dark={dark} brand={brand} layout={cfg === PHONE ? "phone" : "desktop"} />
                           </motion.div>
                        </AnimatePresence>
                     )}
                  </ScaledStage>
               </div>
               <p className="mt-4 text-center font-ticket text-[10px] uppercase tracking-[0.18em] text-lp-paper/40">
                  Live voorbeeld · klik een thema
               </p>
            </Reveal>
         </div>
      </section>
   );
}
